import React, { useCallback, useEffect, useState } from "react";
import api, { authFetch } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import BarraFiltri from "@/components/BarraFiltri";
import BottoneRaccogli from "@/components/BottoneRaccogli";
import { toast } from "sonner";
import {
  Plus, Printer, Send, X, FileText, UploadCloud, Ban, Pen, Download,
  Paperclip, Users, ChevronRight, ChevronLeft,
} from "lucide-react";

/**
 * Il protocollo in uscita: le lettere ufficiali che escono dall'azienda.
 *
 * Stesso impianto dei documenti di trasporto, e per la stessa ragione: il
 * registro e il modulo sono la stessa cosa. Il numero lo assegna il
 * progressivo dell'anno invece di essere letto a occhio sull'ultima riga del
 * foglio Excel — che è il punto in cui due lettere diverse finivano con lo
 * stesso numero.
 *
 * Il corpo si scrive a punti, come su Word: si compila l'ultimo e se ne apre
 * un altro, e con la freccia si rientra di un livello. Chiuso il modulo, la
 * lettera si genera e si scarica pronta da mandare.
 */
const giorno = (iso) => (iso ? iso.substring(0, 10).split("-").reverse().join("/") : "");
const oggi = () => new Date().toISOString().substring(0, 10);

const PUNTO_VUOTO = { testo: "", livello: 0 };

const VUOTO = {
  data: oggi(), autore: "", luogo: "", destinatario: "", destinatario_id: "",
  spettabile: "", alla_attenzione: "", pc: [], oggetto: "", riferimento: "",
  apertura: "", elenco: "numeri", punti: [{ ...PUNTO_VUOTO }],
  chiusura: "", saluti: "Distinti saluti", firmatario: "",
  qualifica_firmatario: "", allegati: [], mezzo: "PEC", note: "",
};

/**
 * Un campo del modulo.
 *
 * Sta fuori dal componente che lo usa, e deve restarci: definito dentro,
 * React lo ricostruirebbe a ogni battuta e il cursore salterebbe fuori dalla
 * casella.
 */
function Campo({ etichetta, chiave, valore, onChange, tipo = "text", lista, aiuto }) {
  return (
    <div>
      <Label>{etichetta}</Label>
      <Input type={tipo} value={valore ?? ""} data-testid={`prot-${chiave}`}
             list={lista ? `lp-${chiave}` : undefined}
             onChange={(e) => onChange(e.target.value)} />
      {lista && (
        <datalist id={`lp-${chiave}`}>
          {lista.map((o) => <option key={o} value={o} />)}
        </datalist>
      )}
      {aiuto && <p className="text-[11px] text-slate-400 mt-0.5">{aiuto}</p>}
    </div>
  );
}

/**
 * Una casella di testo su più righe.
 *
 * Fuori dal componente per la stessa ragione di `Campo`: dentro, il cursore
 * salterebbe a ogni lettera scritta.
 */
function Testo({ etichetta, chiave, valore, onChange, righe = 3, aiuto, segnaposto }) {
  return (
    <div>
      {etichetta && <Label>{etichetta}</Label>}
      <textarea value={valore ?? ""} rows={righe} data-testid={`prot-${chiave}`}
                placeholder={segnaposto}
                onChange={(e) => onChange(e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
      {aiuto && <p className="text-[11px] text-slate-400 mt-0.5">{aiuto}</p>}
    </div>
  );
}

/**
 * Un elenco di righe di testo che si allunga quanto serve: i per conoscenza
 * e gli allegati.
 *
 * Nasce vuoto e non finisce mai: appena si scrive nell'ultima riga se ne apre
 * un'altra, e c'è il pulsante per aggiungerne una prima di aver riempito
 * quella sotto. Le righe vuote non arrivano al documento.
 */
function Righe({ etichetta, chiave, icona: Icona, voci, onChange, segnaposto, aiuto }) {
  const elenco = voci && voci.length ? voci : [""];

  const cambia = (i, v) => {
    const fuori = elenco.map((x, k) => (k === i ? v : x));
    if (fuori[fuori.length - 1]) fuori.push("");
    onChange(fuori);
  };

  return (
    <div className="border border-slate-200 rounded-lg p-3">
      <div className="text-sm font-semibold mb-2 flex items-center gap-2">
        {Icona && <Icona className="w-4 h-4 text-orange-500" />} {etichetta}
      </div>
      <div className="space-y-1.5">
        {elenco.map((v, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <Input value={v || ""} placeholder={segnaposto}
                   data-testid={`prot-${chiave}-${i}`}
                   onChange={(e) => cambia(i, e.target.value)} />
            <button type="button" title="Togli questa riga"
                    onClick={() => onChange(elenco.filter((_, k) => k !== i))}
                    className="text-slate-400 hover:text-red-600 px-1">
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
      <Button type="button" variant="outline" size="sm" className="mt-2"
              data-testid={`prot-aggiungi-${chiave}`}
              onClick={() => onChange([...elenco, ""])}>
        <Plus className="w-4 h-4 mr-1" /> Aggiungi
      </Button>
      {aiuto && <p className="text-[11px] text-slate-400 mt-1.5">{aiuto}</p>}
    </div>
  );
}

function ModuloProtocollo({ rif, prot, onChiudi, onSalvato, onScarica }) {
  const nuovo = !prot;
  // Un documento salvato senza punti torna indietro con l'elenco vuoto, ed è
  // giusto che sia così nel database. A video però serve sempre un punto in
  // cui scrivere: senza, il riquadro resta vuoto e il corpo di quella lettera
  // non si può più scrivere, perché i punti nuovi nascono dall'ultimo.
  const [f, setF] = useState(() => {
    const base = prot ? { ...VUOTO, ...prot } : { ...VUOTO };
    if (!base.punti?.length) base.punti = [{ ...PUNTO_VUOTO }];
    if (!base.luogo) base.luogo = rif?.luogo || "";
    if (!base.firmatario) base.firmatario = rif?.firmatario || "";
    if (!base.qualifica_firmatario) {
      base.qualifica_firmatario = rif?.qualifica_firmatario || "";
    }
    return base;
  });
  const [salvo, setSalvo] = useState(false);
  const su = (k) => (v) => setF((x) => ({ ...x, [k]: v }));

  const cambiaPunto = (i, campo, valore) =>
    setF((x) => {
      const punti = (x.punti || []).map((p, k) => (k === i ? { ...p, [campo]: valore } : p));
      // Scrivendo nell'ultimo punto se ne apre un altro: si scrive di
      // seguito senza toccare «aggiungi» ogni volta.
      const ultimo = punti[punti.length - 1];
      if (!ultimo || ultimo.testo) punti.push({ ...PUNTO_VUOTO });
      return { ...x, punti };
    });

  const aggiungiPunto = () =>
    setF((x) => ({ ...x, punti: [...(x.punti || []), { ...PUNTO_VUOTO }] }));

  const togliPunto = (i) =>
    setF((x) => {
      const punti = (x.punti || []).filter((_, k) => k !== i);
      return { ...x, punti: punti.length ? punti : [{ ...PUNTO_VUOTO }] };
    });

  const rientra = (i, quanto) =>
    setF((x) => ({
      ...x,
      punti: (x.punti || []).map((p, k) => (k === i
        ? { ...p, livello: Math.max(0, Math.min(1, (p.livello || 0) + quanto)) }
        : p)),
    }));

  // Come si vedrà il segno davanti al punto sul foglio. Si mostra qui perché
  // il modulo e il PDF devono raccontare la stessa cosa.
  const segno = (i) => {
    const punti = f.punti || [];
    if ((punti[i]?.livello || 0) > 0) return "–";
    const posizione = punti.slice(0, i + 1).filter((p) => !(p.livello || 0)).length;
    if (f.elenco === "lettere") return `${String.fromCharCode(96 + posizione)})`;
    if (f.elenco === "punti") return "•";
    if (f.elenco === "nessuno") return "";
    return `${posizione}.`;
  };

  const corpo = () => ({
    ...f,
    pc: (f.pc || []).filter((x) => String(x).trim()),
    allegati: (f.allegati || []).filter((x) => String(x).trim()),
    punti: (f.punti || []).filter((p) => String(p.testo || "").trim()),
  });

  const salva = async (poiScarica) => {
    if (!f.destinatario?.trim()) { toast.error("Manca il destinatario"); return; }
    if (!f.oggetto?.trim()) { toast.error("Manca l'oggetto"); return; }
    setSalvo(true);
    try {
      const { data } = nuovo
        ? await api.post("/protocollo", corpo())
        : await api.put(`/protocollo/${prot.id}`, corpo());
      toast.success(nuovo ? `Protocollo ${data.numero} creato` : "Protocollo aggiornato");
      if (poiScarica) await onScarica(data);
      onSalvato(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel salvataggio");
    } finally { setSalvo(false); }
  };

  // Una scorciatoia per non ripetere valore e onChange su venti campi. Non è
  // un componente: è solo l'oggetto delle proprietà, così `Campo` resta lo
  // stesso fra un render e l'altro e il cursore non salta.
  const c = (chiave) => ({ chiave, valore: f[chiave], onChange: su(chiave) });

  return (
    <Dialog open onOpenChange={onChiudi}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {nuovo ? "Nuovo protocollo in uscita"
                   : `Protocollo ${prot.numero}`}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {prot?.annullato && (
            <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800">
              <strong>Documento annullato</strong> da {prot.annullato.da} il{" "}
              {giorno(prot.annullato.il)}.
              <br />Motivo: {prot.annullato.motivo}
              <br />
              <span className="text-xs">
                Resta qui da leggere per intero. Non si corregge più.
              </span>
            </div>
          )}
          {nuovo && (
            <p className="text-xs text-slate-500">
              Il numero di protocollo lo assegna l&apos;app quando salvi: il
              prossimo è il <strong>{rif?.prossimo_numero}</strong>.
            </p>
          )}

          <div className="grid sm:grid-cols-3 gap-3">
            <Campo etichetta="Data del protocollo" {...c("data")} tipo="date" />
            <Campo etichetta="Luogo" {...c("luogo")} />
            <Campo etichetta="Autore" {...c("autore")} lista={rif?.autori || []}
                   aiuto="Se lo lasci vuoto ci va il tuo nome." />
          </div>

          <div className="border border-slate-200 rounded-lg p-3 space-y-3">
            <div className="text-sm font-semibold flex items-center gap-2">
              <Send className="w-4 h-4 text-orange-500" /> A chi va
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Campo etichetta="Destinatario" {...c("destinatario")}
                     lista={[...new Set([...(rif?.clienti || []).map((x) => x.name),
                                         ...(rif?.destinatari || [])])]}
                     aiuto="Il nome corto, quello che si legge a registro." />
              <Campo etichetta="Alla cortese attenzione di" {...c("alla_attenzione")}
                     aiuto="Facoltativo: la persona dentro l'ente." />
            </div>
            <Testo etichetta="Spett.le — come va stampato sulla lettera"
                   chiave="spettabile" valore={f.spettabile}
                   onChange={su("spettabile")} righe={4}
                   aiuto="Ragione sociale e indirizzo, una riga per volta. Se lo lasci vuoto va il nome del destinatario." />
            <Righe etichetta="e p.c. — per conoscenza" chiave="pc" icona={Users}
                   voci={f.pc} onChange={su("pc")}
                   segnaposto="Ente o persona in copia"
                   aiuto="Quanti ne servono. Le righe vuote non vengono stampate." />
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <Campo etichetta="Oggetto" {...c("oggetto")} />
            <div>
              <Label>Trasmesso tramite</Label>
              <select value={f.mezzo || "PEC"} data-testid="prot-mezzo"
                      onChange={(e) => su("mezzo")(e.target.value)}
                      className="w-full h-10 border border-slate-200 rounded-lg px-3 text-sm bg-white">
                {(rif?.mezzi || ["PEC"]).map((x) => (
                  <option key={x} value={x}>{x}</option>
                ))}
              </select>
              <p className="text-[11px] text-slate-400 mt-0.5">
                Serve al registro: fra sei mesi «l&apos;abbiamo mandata?» si
                risponde solo se c&apos;è scritto come.
              </p>
            </div>
          </div>

          <Testo etichetta="Riferimento" chiave="riferimento" righe={2}
                 valore={f.riferimento} onChange={su("riferimento")}
                 segnaposto="Rif. Vostra nota del 12/10/2026 prot. 4412"
                 aiuto="Facoltativo: a che cosa stiamo rispondendo." />

          <div className="border border-slate-200 rounded-lg p-3 space-y-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-sm font-semibold flex items-center gap-2">
                <FileText className="w-4 h-4 text-orange-500" /> Il corpo della lettera
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-[11px] text-slate-500">Segno dei punti</Label>
                <select value={f.elenco || "numeri"} data-testid="prot-elenco"
                        onChange={(e) => su("elenco")(e.target.value)}
                        className="h-9 border border-slate-200 rounded-lg px-2 text-sm bg-white">
                  {(rif?.elenchi || [{ codice: "numeri", nome: "1. 2. 3." }]).map((x) => (
                    <option key={x.codice} value={x.codice}>{x.nome}</option>
                  ))}
                </select>
              </div>
            </div>

            <Testo etichetta="Prima dei punti" chiave="apertura" righe={3}
                   valore={f.apertura} onChange={su("apertura")}
                   segnaposto="Con la presente Vi comunichiamo quanto segue."
                   aiuto="Riga vuota per andare a capoverso. Una lettera che comincia da «1.» non si capisce." />

            <div className="space-y-1.5">
              {(f.punti || []).map((p, i) => (
                <div key={i} className="flex items-start gap-1.5">
                  <span className="w-7 pt-2.5 text-right text-xs font-semibold text-slate-400 tabular-nums shrink-0">
                    {segno(i)}
                  </span>
                  <textarea value={p.testo || ""} rows={2}
                            data-testid={`punto-${i}`}
                            placeholder={i === 0 ? "Scrivi il primo punto" : ""}
                            onChange={(e) => cambiaPunto(i, "testo", e.target.value)}
                            className={`flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm ${
                              (p.livello || 0) ? "ml-6" : ""}`} />
                  <div className="flex flex-col pt-1">
                    {(p.livello || 0) ? (
                      <button type="button" title="Riporta a punto principale"
                              data-testid={`punto-sinistra-${i}`}
                              onClick={() => rientra(i, -1)}
                              className="text-slate-400 hover:text-[#0067b2] px-1">
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                    ) : (
                      <button type="button" title="Rientra come sottopunto"
                              data-testid={`punto-destra-${i}`}
                              onClick={() => rientra(i, 1)}
                              className="text-slate-400 hover:text-[#0067b2] px-1">
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    )}
                    <button type="button" title="Togli questo punto"
                            onClick={() => togliPunto(i)}
                            className="text-slate-400 hover:text-red-600 px-1">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <Button type="button" variant="outline" size="sm"
                    onClick={aggiungiPunto} data-testid="prot-aggiungi-punto">
              <Plus className="w-4 h-4 mr-1" /> Aggiungi punto
            </Button>
            <p className="text-[11px] text-slate-400">
              Appena scrivi nell&apos;ultimo punto se ne apre un altro. La
              freccia a destra lo rientra come sottopunto.
            </p>

            <Testo etichetta="Dopo i punti" chiave="chiusura" righe={3}
                   valore={f.chiusura} onChange={su("chiusura")}
                   segnaposto="Restiamo a disposizione per ogni chiarimento." />
          </div>

          <div className="grid sm:grid-cols-3 gap-3">
            <Campo etichetta="Saluti di chiusura" {...c("saluti")}
                   lista={rif?.saluti || []} />
            <Campo etichetta="Firma" {...c("firmatario")} />
            <Campo etichetta="Qualifica di chi firma" {...c("qualifica_firmatario")} />
          </div>

          <Righe etichetta="Allegati" chiave="allegati" icona={Paperclip}
                 voci={f.allegati} onChange={su("allegati")}
                 segnaposto="Come si chiama l'allegato"
                 aiuto="Vengono stampati numerati in fondo alla lettera." />

          <Testo etichetta="Note per il registro (non si stampano)"
                 chiave="note" righe={2} valore={f.note} onChange={su("note")} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onChiudi}>Chiudi</Button>
          {!prot?.annullato && (
            <>
              <Button variant="outline" onClick={() => salva(false)}
                      disabled={salvo} data-testid="salva-protocollo">
                {salvo ? "Salvo..." : "Salva"}
              </Button>
              <Button onClick={() => salva(true)} disabled={salvo}
                      data-testid="salva-scarica-protocollo"
                      className="bg-orange-500 hover:bg-orange-600 text-white">
                <Download className="w-4 h-4 mr-2" />
                {salvo ? "Salvo..." : "Salva e scarica"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Il caricamento dello storico dal registro Excel.
 *
 * Prima si guarda cosa succederebbe, poi si conferma: su un registro di
 * protocollo caricare due volte lo stesso foglio vorrebbe dire avere due
 * lettere con lo stesso numero, e non si torna indietro. Il numero è il nome
 * proprio del documento, quindi è lui a dire cosa c'è già.
 */
function ImportaStorico({ onFatto, onChiudi }) {
  const [file, setFile] = useState(null);
  const [anno, setAnno] = useState("");
  const [esito, setEsito] = useState(null);
  const [lavoro, setLavoro] = useState(false);

  const manda = async (soloVerifica) => {
    if (!file) { toast.error("Scegli il file"); return; }
    setLavoro(true);
    try {
      const modulo = new FormData();
      modulo.append("file", file);
      modulo.append("solo_verifica", soloVerifica ? "true" : "false");
      if (anno) modulo.append("anno", anno);
      const { data } = await api.post("/protocollo/importa", modulo);
      setEsito(data);
      if (!soloVerifica) {
        toast.success(`${data.importate} protocolli caricati`);
        onFatto();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel caricamento");
    } finally { setLavoro(false); }
  };

  return (
    <Dialog open onOpenChange={onChiudi}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Carica lo storico del protocollo</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm">
            <strong className="text-amber-800">Nessun numero entra due volte.</strong>{" "}
            Il numero è il nome proprio del documento: se è già a registro quella
            riga non entra, e se è ripetuto dentro il file entra una volta sola.
            Dopo il caricamento la numerazione riparte dall&apos;ultimo numero
            visto, così la prima lettera nuova non ci va a sbattere.
            <br />
            Del registro si prendono numero, data, destinatario, oggetto, come è
            stato trasmesso, gli allegati e le note. Il <strong>testo</strong> delle
            lettere lì dentro non c&apos;è: i documenti importati entrano come
            righe di registro, e il corpo si scrive aprendoli.
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>File Excel</Label>
              <Input type="file" accept=".xlsx,.xlsm,.xls"
                     data-testid="file-storico-protocollo"
                     onChange={(e) => { setFile(e.target.files[0]); setEsito(null); }} />
            </div>
            <div>
              <Label>Anno da caricare</Label>
              <Input value={anno} data-testid="anno-storico-protocollo"
                     placeholder="tutti gli anni"
                     onChange={(e) => { setAnno(e.target.value); setEsito(null); }} />
              <p className="text-[11px] text-slate-400 mt-0.5">
                Lascialo vuoto per caricarli tutti.
              </p>
            </div>
          </div>

          {esito && (
            <div className="space-y-2">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[["Nel file", esito.righe_nel_file],
                  ["Dell'anno", esito.righe_dell_anno],
                  ["Da caricare", esito.da_importare],
                  ["Già presenti", esito.gia_presenti]].map(([n, v]) => (
                  <div key={n} className="border border-slate-200 rounded p-2">
                    <div className="font-display text-xl font-black tabular-nums">{v}</div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-wider">{n}</div>
                  </div>
                ))}
              </div>
              {esito.dal && (
                <p className="text-xs text-slate-500">
                  Dal {giorno(esito.dal)} al {giorno(esito.al)}
                  {(esito.anni_nel_file || []).length > 1
                    && ` · nel file ci sono gli anni ${esito.anni_nel_file.join(", ")}`}
                </p>
              )}
              {(esito.gia_a_registro || []).length > 0 && (
                <p className="text-xs text-slate-600">
                  <span className="font-semibold">{esito.gia_a_registro.length}</span>{" "}
                  già a registro:{" "}
                  {esito.gia_a_registro.slice(0, 8).map((x) => x.numero).join(" · ")}
                  {esito.gia_a_registro.length > 8 && " …"}
                </p>
              )}
              {(esito.doppie_nel_file || []).length > 0 && (
                <p className="text-xs text-amber-700">
                  <span className="font-semibold">{esito.doppie_nel_file.length}</span>{" "}
                  numeri ripetuti dentro il file, ne entra uno solo:{" "}
                  {esito.doppie_nel_file.slice(0, 8).map((x) => x.numero).join(" · ")}
                </p>
              )}
              {(esito.scartate || []).length > 0 && (
                <p className="text-xs text-red-700">
                  <span className="font-semibold">{esito.scartate.length}</span>{" "}
                  righe non leggibili:{" "}
                  {esito.scartate.slice(0, 6).map((s) => `riga ${s.riga} (${s.perche})`).join(" · ")}
                </p>
              )}
              {(esito.avvisi || []).map((a, i) => (
                <p key={i} className="text-xs text-slate-500">{a}</p>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onChiudi}>Chiudi</Button>
          <Button variant="outline" onClick={() => manda(true)} disabled={lavoro || !file}
                  data-testid="verifica-storico-protocollo">
            Guarda cosa succede
          </Button>
          <Button onClick={() => manda(false)}
                  disabled={lavoro || !esito || !esito.da_importare}
                  data-testid="carica-storico-protocollo"
                  className="bg-orange-500 hover:bg-orange-600 text-white">
            {esito?.da_importare ? `Carica ${esito.da_importare} protocolli` : "Carica"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * L'annullamento di un protocollo.
 *
 * Il documento non sparisce: resta a registro con una riga sopra. Un numero
 * che sparisce da un registro di protocollo non lo può controllare nessuno —
 * chi si chiede perché fra il 14 e il 16 manchi il 15 non ha modo di saperlo.
 */
function ConfermaAnnullamento({ prot, onChiudi, onConferma }) {
  const [scritto, setScritto] = useState("");
  const [motivo, setMotivo] = useState("");
  const giusto = scritto.trim() === String(prot.numero || "").trim();
  const spiegato = motivo.trim().length >= 5;

  return (
    <Dialog open onOpenChange={onChiudi}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-red-700">
            Annullare il protocollo {prot.numero}?
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <div className="bg-red-50 border border-red-200 rounded p-3 space-y-1.5">
            <p>
              <strong>Resta a registro, barrato.</strong> Si potrà sempre
              riaprire e leggere cosa conteneva, con scritto chi l&apos;ha
              annullato, quando e perché.
            </p>
            <p>
              Il numero <strong>{prot.numero}</strong> resta suo: nessun altro
              documento lo riprenderà.
            </p>
            <p>
              Se la lettera è già uscita, annullarla qui non la richiama
              indietro: serve comunque una comunicazione al destinatario.
            </p>
          </div>

          <div className="text-xs text-slate-600">
            {prot.data && `${giorno(prot.data)} · `}
            {prot.destinatario}{prot.oggetto ? ` · ${prot.oggetto}` : ""}
          </div>

          <div>
            <Label>Perché lo annulli</Label>
            <textarea value={motivo} rows={2} data-testid="motivo-annullamento-prot"
                      placeholder="Es. intestato all'ente sbagliato"
                      onChange={(e) => setMotivo(e.target.value)}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
          </div>

          <div>
            <Label>Riscrivi il numero per confermare</Label>
            <Input value={scritto} placeholder={prot.numero}
                   data-testid="conferma-numero-protocollo"
                   onChange={(e) => setScritto(e.target.value)} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onChiudi}>Lascialo com&apos;è</Button>
          <Button onClick={() => onConferma(motivo.trim())}
                  disabled={!giusto || !spiegato}
                  data-testid="annulla-protocollo-conferma"
                  className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-40">
            <Ban className="w-4 h-4 mr-2" />
            {!spiegato ? "Scrivi la ragione"
              : !giusto ? "Scrivi il numero" : "Annulla il documento"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Protocollo() {
  const [rif, setRif] = useState(null);
  const [righe, setRighe] = useState([]);
  const [totale, setTotale] = useState(0);
  const [carico, setCarico] = useState(true);
  const [anno, setAnno] = useState("");
  const [autore, setAutore] = useState("");
  const [mezzo, setMezzo] = useState("");
  const [cerca, setCerca] = useState("");
  const [aperto, setAperto] = useState(null);   // il protocollo in modifica
  const [nuovo, setNuovo] = useState(false);
  const [importa, setImporta] = useState(false);
  const [daAnnullare, setDaAnnullare] = useState(null);

  const carica = useCallback(async () => {
    setCarico(true);
    try {
      const p = new URLSearchParams();
      if (anno) p.set("anno", anno);
      if (autore) p.set("autore", autore);
      if (mezzo) p.set("mezzo", mezzo);
      if (cerca.trim()) p.set("q", cerca.trim());
      const { data } = await api.get(`/protocollo?${p.toString()}`);
      setRighe(data.protocolli);
      setTotale(data.totale);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel caricamento");
    } finally { setCarico(false); }
  }, [anno, autore, mezzo, cerca]);

  const caricaRiferimenti = useCallback(() => {
    api.get("/protocollo/riferimenti").then(({ data }) => setRif(data)).catch(() => {});
  }, []);

  useEffect(() => { caricaRiferimenti(); }, [caricaRiferimenti]);
  useEffect(() => {
    const t = setTimeout(carica, cerca ? 300 : 0);
    return () => clearTimeout(t);
  }, [carica, cerca]);

  const scarica = async (d) => {
    try {
      // authFetch mette già il prefisso dell'API: rimetterlo qui
      // raddoppierebbe l'indirizzo e la stampa risponderebbe 404.
      const r = await authFetch(`/protocollo/${d.id}/pdf`);
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Errore nella generazione della lettera"); }
  };

  const annullaDavvero = async (d, motivo) => {
    try {
      await api.delete(`/protocollo/${d.id}`, { data: { motivo } });
      toast.success(`Protocollo ${d.numero} annullato`);
      setDaAnnullare(null);
      carica(); caricaRiferimenti();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const chiudiModulo = (aggiornato) => {
    setAperto(null); setNuovo(false);
    if (aggiornato) { carica(); caricaRiferimenti(); }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 pt-20 pb-24 space-y-4"
         data-testid="pagina-protocollo">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#0067b2] mb-1">
            Documenti
          </div>
          <h1 className="font-display text-3xl font-black tracking-tight flex items-center gap-2">
            <Send className="w-7 h-7 text-orange-500" /> Protocollo in uscita
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Le lettere ufficiali che escono dall&apos;azienda. Il registro e il
            modulo sono la stessa cosa: il numero lo assegna l&apos;app, e la
            lettera si scarica pronta da mandare.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="h-12" data-testid="importa-protocollo"
                  onClick={() => setImporta(true)}>
            <UploadCloud className="w-4 h-4 mr-2" /> Carica lo storico
          </Button>
          <Button onClick={() => setNuovo(true)} data-testid="nuovo-protocollo"
                  className="h-12 bg-orange-500 hover:bg-orange-600 text-white">
            <Plus className="w-4 h-4 mr-2" /> Nuovo protocollo
          </Button>
        </div>
      </div>

      <BarraFiltri
        cerca={cerca} onCerca={setCerca}
        segnaposto="Cerca numero, destinatario, oggetto, testo..."
        conteggio={totale} etichetta="protocolli"
        filtri={[
          { valore: anno, onChange: setAnno, tutti: "Tutti gli anni",
            opzioni: rif?.anni || [], testid: "filtro-anno" },
          { valore: autore, onChange: setAutore, tutti: "Tutti gli autori",
            opzioni: rif?.autori || [], testid: "filtro-autore" },
          { valore: mezzo, onChange: setMezzo, tutti: "Tutti i mezzi",
            opzioni: rif?.mezzi || [], testid: "filtro-mezzo" },
        ]} />

      {carico ? (
        <p className="text-sm text-slate-500 py-8 text-center">Caricamento...</p>
      ) : righe.length === 0 ? (
        <Card className="border-dashed border-2"><CardContent className="p-10 text-center">
          <Send className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">Nessun protocollo in uscita.</p>
        </CardContent></Card>
      ) : (
        <Card className="border-slate-200">
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm min-w-[52rem]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                  <th className="p-3">Protocollo</th>
                  <th className="p-3">Data</th>
                  <th className="p-3">Destinatario</th>
                  <th className="p-3">Oggetto</th>
                  <th className="p-3">Trasmesso</th>
                  <th className="p-3">Autore</th>
                  <th className="p-3" />
                </tr>
              </thead>
              <tbody>
                {righe.map((d) => {
                  const ann = d.annullato;
                  return (
                    <tr key={d.id} data-testid={`riga-protocollo-${d.numero}`}
                        className={`border-b border-slate-50 last:border-0 ${
                          ann ? "bg-slate-50/80 text-slate-400 line-through decoration-red-400"
                              : "hover:bg-slate-50/60"}`}>
                      <td className="p-3 font-semibold tabular-nums whitespace-nowrap">
                        <button type="button" onClick={() => setAperto(d)}
                                className={ann ? "text-slate-500 hover:underline"
                                               : "text-[#0067b2] hover:underline"}>
                          {d.numero}
                        </button>
                      </td>
                      <td className="p-3 tabular-nums whitespace-nowrap">{giorno(d.data)}</td>
                      <td className="p-3">
                        {d.destinatario}
                        {(d.pc || []).length > 0 && (
                          <span className="block text-[11px] text-slate-400">
                            e p.c. {(d.pc || []).length}
                          </span>
                        )}
                      </td>
                      <td className="p-3 max-w-[20rem]">
                        {d.oggetto}
                        {ann && (
                          <span className="block no-underline text-[11px] text-red-600 mt-0.5"
                                data-testid={`annullato-prot-${d.numero}`}>
                            Annullato da {ann.da} il {giorno(ann.il)} · {ann.motivo}
                          </span>
                        )}
                      </td>
                      <td className="p-3 text-slate-500 whitespace-nowrap">{d.mezzo}</td>
                      <td className="p-3 text-slate-500">{d.autore}</td>
                      <td className="p-3">
                        <div className="flex items-center gap-1 justify-end no-underline">
                          <Button variant="outline" size="sm" onClick={() => setAperto(d)}
                                  data-testid={`modifica-prot-${d.numero}`}
                                  title={ann ? "Guarda cosa conteneva" : "Modifica"}>
                            <Pen className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => scarica(d)}
                                  data-testid={`scarica-prot-${d.numero}`}
                                  title="Genera e scarica la lettera">
                            <Printer className="w-3.5 h-3.5" />
                          </Button>
                          {/* La graffetta mette la lettera nel raccoglitore,
                              per mandarla insieme agli altri documenti. */}
                          <BottoneRaccogli className="h-8 w-8 p-0"
                                           voce={{ tipo: "generato",
                                                   id: `protocollo:${d.id}`,
                                                   gruppo: "Protocollo in uscita",
                                                   titolo: `Prot. ${d.numero}`,
                                                   sottotitolo: d.oggetto }} />
                          {!ann && (
                            <Button variant="outline" size="sm"
                                    onClick={() => setDaAnnullare(d)}
                                    title="Annulla il documento"
                                    className="text-red-600 hover:bg-red-50">
                              <Ban className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {(nuovo || aperto) && (
        <ModuloProtocollo rif={rif} prot={aperto} onScarica={scarica}
                          onChiudi={() => chiudiModulo(false)}
                          onSalvato={() => chiudiModulo(true)} />
      )}

      {importa && (
        <ImportaStorico onChiudi={() => setImporta(false)}
                        onFatto={() => { carica(); caricaRiferimenti(); }} />
      )}

      {daAnnullare && (
        <ConfermaAnnullamento prot={daAnnullare}
                              onChiudi={() => setDaAnnullare(null)}
                              onConferma={(motivo) => annullaDavvero(daAnnullare, motivo)} />
      )}
    </div>
  );
}
